// Connect or disconnect this user's Microsoft mailbox.
//
// New since the move off Retool. Retool's "Microsoft Email" resource held a
// per-user OAuth grant and refreshed it invisibly, so there was nothing to
// connect. The Inbox reads Microsoft Graph's /me/messages, which needs the
// signed-in user's *own* delegated token, so each person now grants it once
// from here.
//
// Signing in to the Hub is separate and does not involve Microsoft.

import { useCallback, useEffect, useState } from 'react'
import { Loader2, Mail, MailCheck, MailX } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { toast } from '../../lib/shadcn/sonner'

export default function MailboxConnectionSettings() {
  const [connected, setConnected] = useState<boolean | null>(null)
  const [working, setWorking] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/microsoft/status', { credentials: 'same-origin' })
      if (!response.ok) throw new Error(`status ${response.status}`)
      const body = (await response.json()) as { connected: boolean }
      setConnected(body.connected)
    } catch {
      // Treated as not connected; the Connect button is still offered.
      setConnected(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const connect = () => {
    // A full page navigation, not fetch: this is an OAuth consent redirect to
    // Microsoft, which comes back to /api/microsoft/callback.
    const next = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/api/microsoft/connect?next=${next}`)
  }

  const disconnect = async () => {
    setWorking(true)
    try {
      const response = await fetch('/api/microsoft/disconnect', {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!response.ok) throw new Error(`status ${response.status}`)
      toast.success('Mailbox disconnected')
      await refresh()
    } catch (error) {
      toast.error(
        `Could not disconnect: ${error instanceof Error ? error.message : 'unknown error'}`,
      )
    } finally {
      setWorking(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Mailbox connection
        </CardTitle>
        <CardDescription>
          The Inbox reads your Microsoft mailbox to match customer email to orders. Connect your
          account once to turn it on. Only you can see your own mail.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {connected === null ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking…
          </p>
        ) : connected ? (
          <>
            <p className="flex items-center gap-2 text-sm">
              <MailCheck className="w-4 h-4 text-success" />
              Your mailbox is connected.
            </p>
            <Button variant="outline" onClick={() => void disconnect()} disabled={working}>
              {working ? 'Disconnecting…' : 'Disconnect mailbox'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Disconnecting stops the Inbox syncing. Conversations already saved stay in the Hub.
            </p>
          </>
        ) : (
          <>
            <p className="flex items-center gap-2 text-sm">
              <MailX className="w-4 h-4 text-muted-foreground" />
              No mailbox connected, so the Inbox will not sync.
            </p>
            <Button onClick={connect}>Connect Microsoft mailbox</Button>
            <p className="text-xs text-muted-foreground">
              Microsoft will ask you to allow the Hub to read and send mail on your behalf.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}
